import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { 
  ShieldWarning, 
  SignOut, 
  Clock,
  SpinnerGap
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * SupportSessionBanner - Global banner shown during support sessions
 * 
 * This component:
 * - Checks if current session is a support session
 * - Shows a prominent banner with session details
 * - Provides "End Support Session" button
 * - Shows countdown timer
 * - Auto-ends session when expired
 */
export default function SupportSessionBanner() {
  const [sessionStatus, setSessionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ending, setEnding] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  // Check support session status
  const checkStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/support-access/status`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSessionStatus(data);
        if (data.is_support_session && data.remaining_seconds) {
          setRemainingTime(data.remaining_seconds);
        }
      }
    } catch (error) {
      console.error("Failed to check support session status:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial check
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Countdown timer
  useEffect(() => {
    if (!sessionStatus?.is_support_session || remainingTime <= 0) return;

    const timer = setInterval(() => {
      setRemainingTime(prev => {
        const newValue = prev - 1;
        if (newValue <= 0) {
          // Session expired - force end
          handleExpired();
          return 0;
        }
        return newValue;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [sessionStatus?.is_support_session, remainingTime]);

  const handleExpired = async () => {
    toast.warning("Support session expired");
    // Clear support session markers
    try {
      localStorage.removeItem('fsp_support_session');
      localStorage.removeItem('fsp_auth_identity');
    } catch (e) {
      // Ignore
    }
    try {
      await fetch(`${API}/admin/support-access/end`, {
        method: "POST",
        credentials: "include"
      });
    } catch (error) {
      // Ignore errors on auto-end
    }
    window.location.href = "/admin";
  };

  const handleEndSession = async () => {
    setEnding(true);
    try {
      const response = await fetch(`${API}/admin/support-access/end`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        // Clear support session markers
        try {
          localStorage.removeItem('fsp_support_session');
          localStorage.removeItem('fsp_auth_identity');
        } catch (e) {
          // Ignore
        }
        toast.success("Support session ended");
        window.location.href = "/admin/tenants";
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to end session");
      }
    } catch (error) {
      toast.error("Failed to end support session");
    } finally {
      setEnding(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Don't render if not a support session
  if (loading || !sessionStatus?.is_support_session) {
    return null;
  }

  const isLowTime = remainingTime < 120; // Less than 2 minutes

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-[9999] ${
        isLowTime 
          ? "bg-gradient-to-r from-red-600 to-red-700" 
          : "bg-gradient-to-r from-amber-600 to-orange-600"
      } text-white shadow-lg`}
      data-testid="support-session-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Left side - Session info */}
          <div className="flex items-center gap-3">
            <ShieldWarning size={24} weight="fill" className="flex-shrink-0" />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold">SUPPORT SESSION</span>
              <span className="text-white/90">as</span>
              <Badge className="bg-white/20 text-white border-white/30">
                {sessionStatus.target_email}
              </Badge>
              <span className="text-white/80">({sessionStatus.target_role})</span>
              <span className="text-white/60">•</span>
              <span className="text-white/80">{sessionStatus.tenant_name}</span>
            </div>
          </div>

          {/* Right side - Timer and end button */}
          <div className="flex items-center gap-3">
            {/* Countdown Timer */}
            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full ${
              isLowTime ? "bg-white/30" : "bg-white/20"
            }`}>
              <Clock size={16} weight={isLowTime ? "fill" : "regular"} />
              <span className={`font-mono font-bold ${isLowTime ? "animate-pulse" : ""}`}>
                {formatTime(remainingTime)}
              </span>
            </div>

            {/* End Session Button */}
            <Button
              onClick={handleEndSession}
              disabled={ending}
              size="sm"
              className="bg-white text-amber-700 hover:bg-white/90 font-semibold"
              data-testid="end-support-session-btn"
            >
              {ending ? (
                <SpinnerGap size={16} className="animate-spin mr-1" />
              ) : (
                <SignOut size={16} className="mr-1" />
              )}
              End Session
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
